import { Component, Prop, State, Element, h, Method } from '@stencil/core';
import { CommentType, UserComments, Pagination } from './interfaces';
import anime from 'animejs';
import Helper from '../../Helpers/Helper';
import ChatBubbles from '../../assets/svg/chat-bubbles.svg';
@Component({
  tag: 'salla-comments',
  styleUrl: 'salla-comments.scss',
})
export class SallaComments {

  constructor() {

    salla.onReady(() => {
      this.allowLikes = salla.config.get('store.settings.rating.allow_likes');
      this.showRatingSummary = salla.config.get('store.settings.rating.show_rating_summary');
    })
    
    salla.lang.onLoaded(() => {
      this.comment_title = salla.lang.get('blocks.comments.title')
      this.comment_name = salla.lang.get('blocks.comments.comment')
      this.noComments = salla.lang.get('pages.rating.no_ratings')

      const setNestedAsync = (lang, key, value) => {
        return new Promise((resolve) => {
          salla.helpers.setNested(salla.lang.messages[lang], key, value);
          resolve(true);
        });
      };

      const setTranslations = async () => {
        await setNestedAsync('ar.trans', 'blocks.comments.most_helpful', 'الأكثر إفادة');
        await setNestedAsync('en.trans', 'blocks.comments.most_helpful', 'Most helpful');

        this.mostHelpfulLabel = salla.lang.get('blocks.comments.most_helpful');
        this.comment_title = salla.lang.get('blocks.comments.title')
        this.comment_name = salla.lang.get('blocks.comments.comment')
        this.noComments = salla.lang.get('pages.rating.no_ratings')
      };

      setTranslations();
    })
  }

  /**
   * Page or product ID
   */
  @Prop() itemId!: number;

  /**
     * Load more text
     */
  @Prop() loadMoreText: string;

  /**
     * Load more text
     */
  @Prop() hideForm: boolean;

  /**
     * Block Title
     */
  @Prop() blockTitle: string;

  /**
     * Hide Title
     */
  @Prop() hideTitle: boolean;

  /**
   * Comment Type
   */
  @Prop() type: CommentType.PAGE | CommentType.PRODUCT | CommentType.BLOG = CommentType.PAGE;

  /**
   * Show or hide avatar
   */
  @Prop() showFormAvatar: boolean = false;

  /**
 * Hide Bought
 */
  @Prop() hideBought: boolean = false;

  /**
   * Sort comments 
   */
  @Prop() sort: string | 'latest' | 'oldest' | 'bottom_rating' | 'top_rating';

  /**
   * Determines if the comments are testimonials
   */
  @Prop() testimonials: boolean = false;

  @State() comments: UserComments[];
  @State() pagination: Pagination;
  @State() total: number;
  @State() showPlaceholder: boolean;
  @State() nextPage: string;
  @State() mostHelpfulLabel: string;

  private status: any;
  private btnLoader: any;
  private infiniteScroll: any;
  private wrapper: any;

  @Element() host: HTMLElement;

  // Translations
  @State() noComments: string = salla.lang.get('blocks.comments.no_comments');
  @State() comment_title = salla.lang.get('blocks.comments.title')
  @State() comment_name = salla.lang.get('blocks.comments.comment')
  @State() placeholder_text: string;
  @State() showRatingSummary: boolean = salla.config.get('store.settings.rating.show_rating_summary');
  @State() allowLikes = salla.config.get('store.settings.rating.allow_likes');

  // TOOD: it's a good idea to move this into lang.js
  // Pluralize a string based on the count
  private pluralize(phrases: string, count: number): string {
    const options = phrases.split('|');

    const conditions = [
      { condition: count === 0, index: 0 },
      { condition: count === 1, index: 1 },
      { condition: count === 2, index: 2 },
      { condition: count > 2 && count <= 10, index: 3 },
      { condition: count >= 11, index: 4 }
    ];

    const { index } = conditions.find(({ condition }) => condition) || { index: options.length - 1 };

    const selectedOption = options[index];
    return selectedOption.replace(':count', salla.helpers.number(count.toString()))
      .replace(/\{[0-9]+\}/g, '')
      .replace(/\[\d+,\d+\]|\[11,\*\]/g, '');
  }



  // Initiate infinite scroll
  private initiateInfiniteScroll() {
    if (!this.wrapper) {
      console.error('Wrapper is undefined. Cannot initiate infinite scroll.');
      return;
    }

    this.infiniteScroll = salla.infiniteScroll.initiate(this.wrapper, this.wrapper, {
      path: () => this.nextPage,
      history: true,
      nextPage: this.nextPage,
      scrollThreshold: false,
    }, true);

    this.infiniteScroll?.on('request', _response => {
      this.loading();
    });
    this.infiniteScroll?.on('load', response => {
      this.pagination = response.pagination;
      this.nextPage = typeof response.pagination.links == 'object' && !!response.pagination.links.next ? response.pagination.links.next : null;
      this.handleResponse(response).forEach(card => this.wrapper.append(card));

      let items = this.host.querySelectorAll('salla-comment-item:not(.animated):not(.s-comments-item-admin)');
      this.animateItems(items);
      this.loading(false);
    });
    this.infiniteScroll?.on('error', (e) => {
      salla.console.error('Error loading more comments:', e);
    });
  }

  // Show/hide loading
  private loading(isLoading = true) {
    let btnText = this.status?.querySelector('.s-button-text');
    if (btnText) {
      Helper.toggleElementClassIf(btnText, 's-button-hide', 's-button-show', () => isLoading);
      this.btnLoader.style.display = isLoading ? 'inherit' : 'none';
    }
  }

  // Animate newly added items
  private animateItems(items) {
    anime({
      targets: items,
      opacity: [0, 1],
      duration: 1200,
      translateY: [20, 0],
      delay: function (_el, i) {
        return i * 100;
      },
      easing: 'easeOutExpo',
      complete: function (_anim) {
        items.forEach(item => {
          item.classList.add('animated');
        })
      }

    })
  }

  @Method()
  async reload() {
    this.showPlaceholder = false;
    if (this.wrapper) {
      this.wrapper.innerHTML = ""
      let loading = document.createElement('salla-loading');
      this.wrapper.append(loading)
    }
    this.nextPage = null;
    this.loadInitialData();
  }

  // Get comment item HTML
  private getCommentHTML(comment): HTMLSallaCommentItemElement {
    const commentItem = document.createElement('salla-comment-item') as HTMLSallaCommentItemElement;
    commentItem.comment = comment;
    commentItem.hideBought = this.hideBought;
    return commentItem;
  }



  // Parse response and return an array of comment items to be appended to the wrapper
  private handleResponse(response): Array<HTMLElement> {
    return response.data?.map(comment => this.getCommentHTML(comment)) || [];
  }


  componentWillLoad() {
    return salla.onReady()
      .then(() => this.showRatingSummary = salla.config.get('store.settings.rating.show_rating_summary'))
      .then(() => this.loading())
      .then(() => {
        this.hideTitle = this.hideTitle || this.testimonials;
        this.hideForm = this.hideForm || this.testimonials;
        return this.loadInitialData();
      });
  }

  // Load initial data
  private async loadInitialData() {
    try {
      let resp,
        searchParams = new URLSearchParams(window.location.search);
      searchParams.has('sort') && (this.sort = searchParams.get('sort'));
      if (this.testimonials) {
        let params = {
          sort: this.sort,
          type: "store"
        };
        resp = await salla.api.request('reviews', { params }, 'get');
      } else {
        resp = await salla.api.comment.getComments(this.type, this.itemId);
      }

      if (!resp.data || !resp.data.length) {
        this.showPlaceholder = false;
        this.loading(false);
        return;
      }

      this.wrapper && (this.wrapper.innerHTML = "");
      this.comments = resp.data;
      this.pagination = resp.pagination;
      this.total = resp.pagination.total;
      this.nextPage = typeof resp.pagination.links == 'object' && !!resp.pagination.links.next ? resp.pagination.links.next : null;

      setTimeout(() => {
        this.handleResponse(resp).forEach(card => this.wrapper.append(card));
        this.initiateInfiniteScroll(); // Initiate infinite scroll after the initial data is loaded
        let items = this.wrapper.querySelectorAll('salla-comment-item:not(.animated)');
        this.animateItems(items);
      }, 100);
    } catch (error) {
      console.error('Error loading initial data:', error);
      this.showPlaceholder = true;
      this.loading(false);
    }
  }


  // Get next page
  async loadMore() {
    this.infiniteScroll?.loadNextPage();
  }

  render() {
    // We should show a different placeholder for pages and products (WIP)
    if (this.showPlaceholder) {
      return (
        <div>
          {!!this.total && !this.hideTitle ? <h2 class="s-comments-title">{this.blockTitle ? this.blockTitle : this.comment_title}</h2> : ''}
          {!this.hideForm && !this.testimonials ? <salla-comment-form showAvatar={this.showFormAvatar} type={this.type} item-id={this.itemId}></salla-comment-form> : ''}
          <div class="s-comments-placeholder">
            <span innerHTML={ChatBubbles} />
            <p>{this.noComments}</p>
          </div>
        </div>
      )
    }
    return (
      <div class={`s-comments s-comments-${this.testimonials ? 'testimonials' : this.type}`}>
        <div class={`${this.type == CommentType.PAGE ? "s-comments-page-container" : "s-comments-container"}`} >
          {!!this.total && !this.hideTitle ? <h2 class="s-comments-title">{this.blockTitle ? this.blockTitle : this.comment_title}</h2> : ''}
          {!this.hideForm && <salla-comment-form showAvatar={this.showFormAvatar} type={this.type} item-id={this.itemId}></salla-comment-form>}
          {this.showRatingSummary && salla.url.is_page('product.single') ? <salla-reviews-summary itemId={this.itemId}></salla-reviews-summary> : ''}
          <div class={`s-comments-header ${!!this.total ? "has-total" : ""}`}>

            {!!this.total && <h2 class="s-comments-count-label" innerHTML={this.pluralize(this.comment_name, this.total)}></h2>}
            {!!this.total && !this.testimonials && this.type !== CommentType.BLOG ?
              <div class="s-comments-filter-wrapper">
                <label class="s-comments-filter-label"
                  htmlFor="comments-filter">{salla.lang.get('pages.categories.sorting')}</label>
                <select id="comments-filter" class="s-form-control s-comments-sort-input" onChange={(e: Event) => {
                  this.sort = (e.target as HTMLSelectElement).value;
                  this.reload()
                }}>
                  <option value="latest" selected>{salla.lang.get("pages.testimonials.sort_by_date_desc")}</option>
                  <option value="oldest">{salla.lang.get("pages.testimonials.sort_by_date_asc")}</option>
                  {this.allowLikes && <option value="most_helpful">{this.mostHelpfulLabel}</option>}
                </select>
              </div>
              : ''}
          </div>          
          <div ref={wrapper => this.wrapper = wrapper}></div>

          {this.nextPage && (
            <div class="s-infinite-scroll-wrapper" ref={status => this.status = status}>
              <button onClick={() => this.loadMore()} class="s-infinite-scroll-btn s-button-btn s-button-primary" type="button">
                <span class="s-button-text s-infinite-scroll-btn-text">{this.loadMoreText ? this.loadMoreText : salla.lang.get('common.elements.load_more')}</span>
                <span class="s-button-loader s-button-loader-center s-infinite-scroll-btn-loader" ref={btnLoader => this.btnLoader = btnLoader} style={{ "display": "none" }}></span>
              </button>
            </div>
          )}
        </div>
      </div>

    );
  }

}
